import { useEffect, useRef, useMemo } from 'react';
import { generatePath, Outlet, useParams, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  Filters,
  useFilters,
  useGetList,
  usePrevious,
  SchemaFilterType,
  SelectItemProps,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  marginListColumns,
  marginListSchema,
  marginListFiltersSchema,
  marginListColumnsLabels,
} from './variables/list';
import {
  useLazyGetMarginsQuery,
  useLazyGetDefaultMarginQuery,
} from './store/services';
import { MarginItem, TariffClientType } from './types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const formatMarginList = (
  list: MarginItem[],
  currency: string,
  riskScoreRanges?: SelectItemProps[],
): MarginItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    result.url = generatePath(ROUTES.MARGIN_INFO, {
      currency,
      marginId: result.id,
    });

    const currentRiskScore = riskScoreRanges?.find(
      (risk) => risk.value === result?.tariff?.riskScoreRangeId,
    );

    const render: Record<string, React.JSX.Element> = {};

    let tariff = '-';

    if (result.tariff) {
      if (result.tariff?.type === TariffClientType.CORPORATE) {
        tariff = 'Corporate';
      } else {
        tariff = 'Individual';
      }
    }

    let feesSettings = <div className={cn.defaultFee}>DEFAULT</div>;

    if (result.isCustom && result.tariffId && !result.organizationId) {
      feesSettings = <div className={cn.customFee}>CUSTOM</div>;
    } else if (result.isCustom && result.organizationId && !result.tariffId) {
      feesSettings = <div className={cn.userFee}>USER</div>;
    }

    render.currencyFromId = <div>{result.currencyFrom.name}</div>;
    render.currencyToId = <div>{result.currencyTo.name}</div>;
    render.rateSourceId = <div>{result.rateSource.name}</div>;
    render.tariffId = <div>{result?.tariff?.name || '-'}</div>;
    render.tariff = <div>{tariff}</div>;
    render.riskScoreRangeId = (
      <div>
        {currentRiskScore
          ? `${currentRiskScore?.name} (${currentRiskScore?.description})`
          : '-'}
      </div>
    );

    render.isCustom = <div>{feesSettings}</div>;

    result.render = render;

    return result;
  });

  return nextList;
};

const MarginList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    margins: {
      list: { margins, pagination, errorsList },
    },
    exchangeDictionaries: { currencies, rateSource, tariffs, riskScoreRanges },
    root: { settings },
  } = useAppSelector((state) => state);

  const { currency } = useParams();
  const { search } = useLocation();

  const prevCurrency = usePrevious(currency);

  const { filters, sort, onFilterChange } = useQueryState(marginListSchema);
  const [getList, { isLoading, isFetching: requestInProgress }] =
    useLazyGetMarginsQuery();
  const [getDefaultMargin] = useLazyGetDefaultMarginQuery();

  const formattedData = useMemo(() => {
    const data = formatMarginList(
      margins as MarginItem[],
      currency as string,
      riskScoreRanges,
    );

    return data;
  }, [margins, sort, filters]);

  const { onFilter, setPage, errorList } = useGetList<MarginItem>({
    getList,
    onFilterChange,
    onSortChange: () => {},
    schema: marginListSchema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: marginListFiltersSchema,
    filters,
    onFilterChange: () => {},
  });

  useScroll({
    element: scrollRef,
    requestInProgress,
  });

  useEffect(() => {
    getDefaultMargin();
  }, []);

  useEffect(() => {
    if (
      filters &&
      !margins &&
      settings &&
      Object.keys(filters).length &&
      rateSource
    ) {
      const currentFilters: Filters = {
        ...filters,
        currencyFromId: {
          value: [currency as string],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(currentFilters);
    } else if (
      ((prevCurrency && currency !== prevCurrency) ||
        (!margins && !Object.keys(filters).length)) &&
      settings &&
      rateSource
    ) {
      const baseRateSource = settings?.find(
        (item) => item.key === 'EXCHANGE-RATE-SOURCE',
      );

      const nextFilters: Filters = {
        currencyFromId: {
          value: [currency as string],
          type: SchemaFilterType.STRING,
        },
      };

      if (!margins) {
        nextFilters.rateSourceId = {
          value: [baseRateSource?.data.value as string],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search.replace('?', ''));

        if (Object.keys(parsed).length) {
          if (parsed.rateSourceId) {
            delete nextFilters.rateSourceId;
          }

          Object.keys(parsed).forEach((item) => {
            nextFilters[item] = {
              value: [parsed[item] as string],
              type: SchemaFilterType.STRING,
            };
          });
        }
      }

      onFilterChange(nextFilters);
      onFilter(nextFilters);
    }
  }, [
    filters,
    sort,
    margins,
    currency,
    prevCurrency,
    search,
    settings,
    rateSource,
  ]);

  useEffect(() => {
    if (currencies) {
      const formattedCurrencies = currencies
        .map((item) => ({
          name: `${item.name} (${item.description})`,
          value: item.id,
        }))
        .filter((item) => item.value !== currency);

      setValues('currencyToId', formattedCurrencies);
    }

    if (rateSource) {
      setValues(
        'rateSourceId',
        rateSource.map((item) => ({ name: item.name, value: item.id })),
      );
    }

    if (tariffs) {
      setValues('tariffId', tariffs);
    }
  }, [currencies, currency, rateSource, tariffs]);

  useEffect(() => {
    let title = 'Exchange margins';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.marginsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={marginListColumns}
        error={errorsList || errorList}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={marginListColumnsLabels}
        onFilterChange={(filterValues) =>
          onFilter({
            ...filterValues,
            currencyFromId: {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            },
          })
        }
        tableBodyClassName={`${!margins?.length ? cn.emptyTable : ''} ${
          requestInProgress ? cn.tableLoading : ''
        }`}
        tableTheadClassName={cn.marginsTableHead}
        filtersComponent={filtersComponent}
        isLoading={requestInProgress}
        handleSubmit={handleSubmit}
        pagination={pagination}
        take="20"
        setPage={(data) =>
          setPage({
            ...data,
            filters: {
              ...filters,
              currencyFromId: {
                value: [currency as string],
                type: SchemaFilterType.STRING,
              },
            },
          })
        }
        clickable
      />
    );
  }

  return (
    <div className={cn.marginsWrapper} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default MarginList;
